import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { publicApi } from "@/lib/api";

export default function ContactDialog({
  open,
  onOpenChange,
  submissionId,
  defaultName = "",
  defaultEmail = "",
  trigger,
}) {
  const [name, setName] = useState(defaultName);
  const [email, setEmail] = useState(defaultEmail);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  // Sync incoming defaults once the parent's submission data loads.
  useEffect(() => {
    setName((prev) => (prev ? prev : defaultName));
  }, [defaultName]);

  useEffect(() => {
    setEmail((prev) => (prev ? prev : defaultEmail));
  }, [defaultEmail]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Please complete every field.");
      return;
    }
    setSending(true);
    try {
      await publicApi.post("/contact", {
        full_name: name.trim(),
        email: email.trim(),
        subject: subject.trim() || null,
        message: message.trim(),
        submission_id: submissionId || null,
      });
      toast.success("Message sent — our studio will reply within two days.");
      setSubject("");
      setMessage("");
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send your message.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger}
      <DialogContent
        data-testid="contact-dialog"
        className="bg-[#FAF9F6] border border-[#E3DED5] sm:max-w-lg p-0"
      >
        <div className="p-6 lg:p-8">
          <DialogHeader>
            <div className="eyebrow">Write to the studio</div>
            <DialogTitle className="font-serif-display text-3xl text-[#2A1F1A] mt-2">
              A note to our team
            </DialogTitle>
            <DialogDescription className="text-sm text-[#6E6E70]">
              Share a question, an update or anything you&apos;d like us to know. We
              reply within two working days.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="mt-6 space-y-5" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="eyebrow">Name</Label>
                <Input
                  data-testid="contact-name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="editorial-input mt-2"
                />
              </div>
              <div>
                <Label className="eyebrow">Email</Label>
                <Input
                  data-testid="contact-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="editorial-input mt-2"
                />
              </div>
            </div>

            <div>
              <Label className="eyebrow">Subject</Label>
              <Input
                data-testid="contact-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. a quick question about my application"
                className="editorial-input mt-2"
              />
            </div>

            <div>
              <Label className="eyebrow">Message</Label>
              <Textarea
                data-testid="contact-message"
                required
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us anything you&apos;d like us to consider."
                className="mt-2 bg-transparent border border-[#E3DED5] focus-visible:ring-0 focus-visible:border-[#C03028] rounded-none shadow-none p-3 font-sans-body"
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit"
              className="btn-primary w-full inline-flex items-center justify-center gap-2 py-3 text-sm tracking-wide rounded-none"
            >
              {sending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Send size={14} />
              )}
              {sending ? "Sending…" : "Send message"}
            </button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
